import React, { useState, useEffect, useCallback } from "react";
import DatesPicker from "../DatesPicker";
import "../../components/DashBoardPage/DashBoard.css";
import "../../components/DashBoardPage/bootstrap-multiselect.css";
import "./OptionsNav.css";

import { useSelector, useDispatch } from "react-redux";
import {
  updateIsShowingWorkers,
  updateAllDates,
  updateApprovalStatus,
  updateAllFuture,
  updateSelectedAuditOption,
} from "../../stores/actions";
import {
  initializeFilters,
  selectWorkers,
  selectProjects,
  updateDatesAndWorkers,
  clearFilterState as clearFilterStateAction,
} from "../../stores/actions/filtersActions";
import ManagerOptions from "./ManagerOptions";
import AuditOptions from "./AuditOptions";
import AllDatesCheckbox from "./AllDatesCheckbox";
import AllFutureCheckbox from "./AllFutureCheckbox";

const OptionsNav = () => {
  const [allProjects, setAllProjects] = useState(true);
  const dispatch = useDispatch();

  const {
    isShowingWorkers,
    startDate,
    endDate,
    managerWorkers,
    managerProjects,
    selectedProjectsIds,
    currentPage,
    allDates,
    allDatesEnabled,
    selectedProjectsObjects,
    selectedWorkersObjects,
    workers,
    projects,
    approvalStatus,
    allFuture,
    selectedAuditOption,
    isInAuditGroup,
  } = useSelector((state) => ({
    isShowingWorkers: state.workers.isShowingWorkers,
    startDate: state.dates.startDate,
    endDate: state.dates.endDate,
    managerWorkers: state.workers.managerWorkers,
    managerProjects:
      state.general.currentPage === "Dashboard"
        ? state.projects.allManagerProjects
        : state.projects.managerProjects,
    selectedProjectsIds: state.projects.selectedProjectsIds,
    currentPage: state.general.currentPage,
    allDates: state.dates.allDates,
    allDatesEnabled: state.dates.allDatesEnabled,
    selectedProjectsObjects: state.projects.selectedProjectsObjects,
    selectedWorkersObjects: state.workers.selectedWorkersObjects,
    workers: state.workers.workers,
    projects: state.projects.projects,
    approvalStatus: state.freeDays.approvalStatus,
    allFuture: state.dates.allFuture,
    selectedAuditOption: state.projects.selectedAuditOption,
    isInAuditGroup: state.general.userGroups.includes(2),
  }));

  const onSelectWorkers = useCallback(
    (selectedWorkers) => {
      dispatch(selectWorkers(selectedWorkers));
    },
    [dispatch]
  );

  const onSelectProjects = useCallback(
    (selectedProjects) => {
      dispatch(selectProjects(selectedProjects, allProjects));

      if (selectedProjects === null) {
        setAllProjects(false);
      } else if (Array.isArray(selectedProjects)) {
        const diff = projects.filter(
          (firstObj) =>
            !selectedProjects.some(
              (secondObj) => firstObj.value === secondObj.value
            )
        );

        if (diff.length === 1 && allProjects) {
          setAllProjects(false);
        }
        if (diff.length === 0) {
          setAllProjects(true);
        }
      }
    },
    [dispatch, allProjects, projects]
  );

  const onSelectApprovalStatus = useCallback(
    (status) => {
      dispatch(updateApprovalStatus(status));
    },
    [dispatch]
  );

  const setActive = useCallback(
    (what, event) => {
      let newIsShowingWorkers = isShowingWorkers;
      let shouldStopEvent = true;
      if (what === "workers") {
        if (newIsShowingWorkers) {
          shouldStopEvent = false;
        }
        newIsShowingWorkers = true;
      } else {
        if (!newIsShowingWorkers) {
          shouldStopEvent = false;
        }
        newIsShowingWorkers = false;
      }

      dispatch(updateIsShowingWorkers(newIsShowingWorkers));
      return !shouldStopEvent;
    },
    [dispatch, isShowingWorkers]
  );

  const handleSetActive = useCallback(() => {
    isShowingWorkers ? setActive("projects") : setActive("workers");
  }, [isShowingWorkers, setActive]);

  const clearFilterState = useCallback(() => {
    dispatch(clearFilterStateAction());
  }, [dispatch]);

  useEffect(() => {
    window.addEventListener("beforeunload", clearFilterState);

    return () => {
      window.removeEventListener("beforeunload", clearFilterState);
    };
  }, [clearFilterState]);

  useEffect(() => {
    dispatch(initializeFilters());
  }, [dispatch]);

  const onDatesChanged = useCallback(
    async (newStartDate, newEndDate) => {
      if (!newStartDate) {
        newStartDate = startDate;
      }
      if (!newEndDate) {
        newEndDate = endDate;
      }
      if (selectedWorkersObjects === undefined) {
        return;
      }

      dispatch(updateDatesAndWorkers(newStartDate, newEndDate));
    },
    [dispatch, startDate, endDate, selectedWorkersObjects]
  );

  const onToggleAllDates = useCallback(() => {
    dispatch(updateAllDates(!allDates));
  }, [dispatch, allDates]);

  const onToggleAllFuture = useCallback(() => {
    dispatch(updateAllFuture(!allFuture));
  }, [dispatch, allFuture]);

  const onSelectAuditOption = useCallback(
    (val) => {
      dispatch(updateSelectedAuditOption(val));
    },
    [dispatch]
  );

  if (
    (currentPage === "Planner" && managerWorkers?.Workers) || // in planner page we don't care about projects
    (managerWorkers?.Workers && managerProjects?.length !== 0) ||
    currentPage === "Admin"
  ) {
    return (
      <span className="optionNavContainer d-flex align-items-center flex-wrap mb-2">
        {currentPage !== "Admin" && (
          <ManagerOptions
            currentPage={currentPage}
            isShowingWorkers={isShowingWorkers}
            workers={workers}
            selectedWorkersObjects={selectedWorkersObjects}
            onSelectWorkers={onSelectWorkers}
            projects={projects}
            selectedProjectsObjects={selectedProjectsObjects}
            onSelectProjects={onSelectProjects}
            approvalStatus={approvalStatus}
            onSelectApprovalStatus={onSelectApprovalStatus}
            handleSetActive={handleSetActive}
          />
        )}
        <DatesPicker
          startDate={startDate}
          endDate={endDate}
          onDatesChanged={onDatesChanged}
          allDates={allDates}
          currentPage={currentPage}
          allFuture={allFuture}
        />
        {currentPage === "Hours" && (
          <AllDatesCheckbox
            allDates={allDates}
            allDatesEnabled={allDatesEnabled}
            onToggleAllDates={onToggleAllDates}
          />
        )}

        {isInAuditGroup &&
          currentPage !== "Planner" &&
          currentPage !== "Admin" &&
          (selectedProjectsIds[0] < 1000 || isShowingWorkers) && (
            <AuditOptions
              currentPage={currentPage}
              selectedAuditOption={selectedAuditOption}
              onSelectAuditOption={onSelectAuditOption}
            />
          )}
        {currentPage === "Planner" && (
          <AllFutureCheckbox
            allFuture={allFuture}
            onToggleAllFuture={onToggleAllFuture}
          />
        )}
      </span>
    );
  } else {
    return <div></div>;
  }
};

export default OptionsNav;
